import { createAsyncCategoryHandler } from "../category-dispatcher";
import type { ToolInput } from "../types";
import { advancedTools } from "./advanced-tools";
import { projectColorTools } from "./project-color-tools";
import { reviewTools } from "./review-tools";
import { screenshotTools } from "./screenshot-tools";
import { styleTools } from "./style-tools";

/**
 * Dispatches `manage_color` to add / update / delete project-color handlers
 * based on the `colorAction` field in the input.
 * Because `update` is async, the entire wrapper must be async.
 */
async function manageColorDispatch(input: ToolInput): Promise<unknown> {
  const { colorAction } = input;
  switch (colorAction) {
    case "add":
      return projectColorTools.sync!.ui_style_add_project_color(input);
    case "update":
      return projectColorTools.async!.ui_style_update_project_color(input);
    case "delete":
      return projectColorTools.sync!.ui_style_delete_project_color(input);
    default:
      return { error: `Unknown colorAction: '${colorAction}'. Expected 'add', 'update', or 'delete'.` };
  }
}

/**
 * Category handler for style, review, screenshot, and project-color operations.
 * Consolidates style-tools, advanced-tools, review-tools, screenshot-tools,
 * and project-color-tools handlers.
 */
export const handleStyleCategory = createAsyncCategoryHandler(
  {
    set: styleTools.sync!.ui_style_set_component_styles,
    get_definition: styleTools.sync!.ui_style_get_component_definition,
    get_defaults: advancedTools.sync!.ui_style_get_default_styles,
    review: reviewTools.sync!.ui_style_review_screen_design,
    apply_fixes: reviewTools.sync!.ui_style_apply_design_fixes,
    list_colors: projectColorTools.sync!.ui_style_list_project_colors,
    list_unified: styleTools.sync!.ui_style_list_project_unified_styles,
    apply_unified: styleTools.sync!.ui_style_apply_unified_to_component,
    apply_effect_preset: styleTools.sync!.ui_style_apply_effect_preset,
    copy_styles: styleTools.sync!.ui_style_copy_component_styles,
    apply_theme: styleTools.sync!.ui_style_apply_theme
  },
  {
    screenshot: screenshotTools.async!.ui_style_capture_screenshot,
    manage_color: manageColorDispatch
  }
);
